/**
 * One place to turn the skipper's expected-speed setting into a timed route
 * summary, so the build HUD, the route editor and the Live data fields all read
 * the same way. It just wraps summariseRouteTimed with the SOG/STW choice:
 *
 *   - SOG: the speed already has the tide in it, so the time is the flat
 *     distance / speed, using the live over-ground speed when we're moving and
 *     the cruising speed when we're stopped.
 *   - STW: the boat's speed through the water, so we fold the tidal stream into
 *     each leg (progressive, walking the clock from now) to get the real time on
 *     the ground.
 *
 * See summariseRouteTimed in routing/route.ts and planSpeedRef in store/settings.ts.
 */
import { summariseRouteTimed, type Route, type TimedRouteSummary } from '@/routing';

export interface PlanSpeedInput {
  /** How the cruising speed is meant: over the ground, or through the water. */
  planSpeedRef: 'sog' | 'stw';
  /** The skipper's cruising/planning speed, knots. */
  cruiseSpeedKn: number;
  /** Live speed over the ground, knots (0 when the boat is stopped). */
  sog: number;
}

/**
 * The timed summary for a route on the skipper's chosen speed basis. In SOG mode
 * we prefer the live over-ground speed and fall back to the cruising speed when
 * stopped; in STW mode we always time on the cruising speed through the water and
 * fold in the tide.
 */
export function routeTimedSummary(
  route: Route,
  input: PlanSpeedInput,
  nowMs: number = Date.now(),
): TimedRouteSummary {
  const { planSpeedRef, cruiseSpeedKn, sog } = input;
  return planSpeedRef === 'stw'
    ? summariseRouteTimed(route, { ref: 'stw', knots: cruiseSpeedKn }, { departMs: nowMs })
    : summariseRouteTimed(route, { ref: 'sog', knots: sog > 0 ? sog : cruiseSpeedKn || 0 });
}

/**
 * Format a total-hours value as a compact time-to-go, e.g. "2h 05m". Returns
 * null when there's no usable speed (no time computed), so a caller can show
 * "Set a speed" rather than a NaN once the boat is stopped and no cruising speed
 * is set.
 */
export function formatTtg(totalHours: number | undefined): string | null {
  if (totalHours === undefined || !Number.isFinite(totalHours)) return null;
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  // Carry a 60 back up to the hour so we never print "1h 60m".
  const hh = m === 60 ? h + 1 : h;
  const mm = m === 60 ? 0 : m;
  return `${hh}h ${String(mm).padStart(2, '0')}m`;
}
